import { ErrorMessage, useField, useFormikContext } from 'formik';
import type { ComponentProps, ReactNode } from 'react';
import { memo } from 'react';

import { CustomErrorMessage } from '@src/design-system/error-message';
import { cn } from '@src/design-system/helpers';
import { inputVariants } from '@src/design-system/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@src/design-system/select';
import { formatTextForDisplay } from '@shared/utils/helpers/format-text-for-display';
import InfiniteScroll from '@src/design-system/infinite-scroll';
import { Loader2 } from 'lucide-react';

type TSharedSelectFieldProps = {
  selectPlaceholder?: string;
  selectName: string;
  hasNextPage?: boolean;
  isPending?: boolean;
  className?: string;
  fetchNextPage?: () => unknown;
  onChange?: (value: string) => void;
} & ComponentProps<typeof Select>;

type SelectOption = { label: string; value: string } | string;

type SelectFieldProps<TOption extends SelectOption> =
  TSharedSelectFieldProps & {
    options: TOption[];
    renderItem?: (item: TOption) => ReactNode;
  };

const SelectFieldInner = <TOption extends SelectOption>({
  selectPlaceholder,
  options,
  selectName,
  onChange,
  hasNextPage,
  isPending,
  fetchNextPage,
  className,
  renderItem,
  ...props
}: SelectFieldProps<TOption>) => {
  const { setFieldValue } = useFormikContext();
  const [selectFieldProps, selectFieldMeta] = useField(selectName);

  return (
    <>
      <Select
        value={selectFieldProps.value || ''}
        onValueChange={(value) => {
          setFieldValue(selectName, value);
          onChange?.(value);
        }}
        {...props}
      >
        <SelectTrigger
          hasError={!!selectFieldMeta.error && selectFieldMeta?.touched}
          className={cn('cursor-pointer', inputVariants(), className)}
        >
          <SelectValue placeholder={selectPlaceholder} />
        </SelectTrigger>
        <SelectContent
          className='max-h-[300px]'
          align='end'
        >
          {options.map((opt) => {
            const isOptString = typeof opt === 'string';

            const optValue = isOptString ? opt : opt.value;
            const optLabel = isOptString ? opt : opt.label;

            const Item = renderItem
              ? renderItem(opt)
              : formatTextForDisplay(optLabel);

            return (
              <SelectItem
                key={optValue}
                value={optValue}
              >
                {Item}
              </SelectItem>
            );
          })}

          {fetchNextPage && (
            <InfiniteScroll
              hasMore={!!hasNextPage}
              isLoading={!!isPending}
              next={fetchNextPage}
            >
              {hasNextPage && (
                <div className='w-full flex items-center justify-center mt-4'>
                  <Loader2
                    className='animate-spin text-primary-1000'
                    size={24}
                  />
                </div>
              )}
            </InfiniteScroll>
          )}
        </SelectContent>
      </Select>
      <ErrorMessage
        name={selectName}
        component={CustomErrorMessage}
      />
    </>
  );
};

export const SelectField = memo(SelectFieldInner) as <
  TOption extends SelectOption,
>(
  props: SelectFieldProps<TOption>
) => JSX.Element;
